import { useMemo, useState } from 'react';
import { IoIosArrowForward } from 'react-icons/io';
import { useTaskContext } from '../../../../context/TaskContext';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { GET_COMPANY, GET_ME } from '../../../../apollo/Queries';


const DEFAULT_PROGRESS_OPTIONS = [
    { title: 'Not Defined', percentage: 0, serial: 0 },
    { title: 'Stage 1', percentage: 25, serial: 1 },
    { title: 'Stage 2', percentage: 50, serial: 2 },
    { title: 'Stage 3', percentage: 75, serial: 3 },
    { title: 'Final stage', percentage: 100, serial: 4 }
];
const DEFAULT_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Canceled'];

const STATIC_STATUS_BG = {
    'Not Started': '#ECEEF4',
    'In Progress': '#D7E1F5',
    'Completed': '#C6E9D8',
    'On Hold': '#EEDEC6',
    'Canceled': '#EBC8C8'
};

/* ---------------- Dropdown (Demo Only) ---------------- */
const Dropdown = ({ label, options, selected, selectedValue, onSelect, type, getBackgroundColor }) => {
    const { isObserver } = useTaskContext()
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (item) => {
        if (type === 'progress') {
            onSelect(type, Number(item.percentage) || 0);
        } else {
            onSelect(type, item.title || item);
        }
        setIsOpen(false);
    };

    // Get background color - either from dynamic colors or static fallback
    const getBgColor = (statusName) => {
        if (getBackgroundColor) {
            return getBackgroundColor(statusName);
        }
        // Fallback to static colors for progress or when no getBackgroundColor provided
        if (type === 'progress') return '#ECEEF4';
        return STATIC_STATUS_BG[statusName] || '#ECEEF4';
    };

    return (
        <div className="status_area w-full">
            <p className="text-[12px] text-gray-600 mb-1">{label}</p>
            <div className="relative">
                <div className={`flex items-center justify-between rounded text-[12px] w-full text-teal-600 h-[42px] py-1 px-2.5 cursor-pointer`}
                    style={{ backgroundColor: getBgColor(selected) }}
                    onClick={() => isObserver() ? null : setIsOpen(!isOpen)}
                >
                    {selected}
                    <IoIosArrowForward className={`text-teal-600 text-base transition ${isOpen ? 'rotate-90' : ''}`} />
                </div>

                {isOpen && (
                    <div className="absolute p-2 border rounded w-full bg-white z-50">
                        {type === 'progress' ? (
                            options.map(option => (
                                <div
                                    key={`${option.serial}-${option.title}`}
                                    className={`text-[12px] rounded px-2 py-1 cursor-pointer  ${Number(selectedValue) === Number(option.percentage) ? 'bg-teal-200 text-teal-600' : 'text-teal-600'}`}
                                    id={option.title}
                                    onClick={() => handleSelect(option)}
                                >
                                    <span className={`dropdown-item-dot ${Number(selectedValue) === Number(option.percentage) ? 'selected' : 'false'}`}></span>
                                    {option.title}

                                </div>
                            ))
                        ) : (
                            options.map((item) => {
                                const itemTitle = item.title || item;
                                const itemColor = item.color || STATIC_STATUS_BG[itemTitle] || '#ECEEF4';
                                return (
                                    <div
                                        key={itemTitle}
                                        className={`text-[12px] rounded px-2 py-1 cursor-pointer  ${selected === itemTitle ? 'bg-teal-200 text-teal-600' : 'text-teal-600 hover:bg-teal-50'}`}
                                        id={itemTitle}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <span className={`dropdown-item-dot ${selected === itemTitle ? 'selected' : 'false'}`} style={{ backgroundColor: itemColor }}></span>
                                        {itemTitle}

                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div >
        </div >
    );
};

/* ---------------- Main Component (Demo Only) ---------------- */
const TaskStatusAndProgressNew = () => {

    // const { OnUpdateTask } = useTaskContext();
    // const { active_task } = useSelector(state => state.task);
    // const statusOptions = { 'Not Started': 'Not Started', 'In Progress': 'In Progress', 'Completed': 'Completed', 'On Hold': 'On Hold', 'Canceled': 'Canceled' };
    // const progressOptions = { 0: 'Not Defined', 25: 'Stage 1', 50: 'Stage 2', 75: 'Stage 3', 100: 'Final stage' };

    // const updateOnSelect = (type, value) => {
    //     if (type === 'status') {
    //         OnUpdateTask({ status: value, save_type: type })
    //     } else {
    //         OnUpdateTask({ progress: value, save_type: type })
    //     }
    // }

    const { OnUpdateTask } = useTaskContext()
    const { active_task } = useSelector(state => state.task);

    // Get user data via useQuery to get company_id
    const { data: meData } = useQuery(GET_ME, {
        fetchPolicy: 'cache-first'
    });
    const companyId = meData?.me?.company_id;

    // Query company data for dynamic task_progress
    const { data: companyData, loading: companyLoading } = useQuery(GET_COMPANY, {
        variables: { company_id: companyId },
        skip: !companyId,
        fetchPolicy: 'no-cache'
    });

    // Build dynamic progress options from company task_progress (percentage-based)
    const progressOptions = useMemo(() => {
        const company = companyData?.data?.company || companyData?.company;
        const taskProgress = company?.task_progress;

        if (taskProgress && Array.isArray(taskProgress) && taskProgress.length > 0) {
            const sorted = [...taskProgress].sort((a, b) => (a.serial || 0) - (b.serial || 0));
            let accumulatedWeight = 0;
            return sorted.map((item, index) => {
                const weight = Number(item.weight) || 0;
                accumulatedWeight += weight;
                const percentage = Math.min(accumulatedWeight, 100);
                return ({
                    title: item.title || `Stage ${index + 1}`,
                    percentage,
                    serial: Number(item.serial) || index
                });
            });
        }

        // Default options if no company data
        return DEFAULT_PROGRESS_OPTIONS;
    }, [companyData]);

    const statusOptions = useMemo(() => {
        const company = companyData?.data?.company || companyData?.company;
        const taskStatus = company?.task_status;
        if (taskStatus && Array.isArray(taskStatus) && taskStatus.length > 0) {
            return [...taskStatus]
                .sort((a, b) => (a.serial || 0) - (b.serial || 0))
                .map((item) => ({
                    title: item.title,
                    color: item.color || ''
                }))
                .filter(item => item.title);
        }
        return DEFAULT_STATUS_OPTIONS.map(title => ({ 
            title, 
            color: STATIC_STATUS_BG[title] || '' 
        }));
    }, [companyData]);

    const selectedProgress = useMemo(() => {
        const currentValue = Number(active_task?.progress ?? 0);
        return progressOptions.find(option => Number(option.percentage) === currentValue)
            || progressOptions[0]
            || DEFAULT_PROGRESS_OPTIONS[0];
    }, [active_task?.progress, progressOptions]);

    const updateOnSelect = (type, value) => {
        if (type === 'status') {
            OnUpdateTask({ status: value, save_type: type })
        } else {
            OnUpdateTask({ progress: value, save_type: type })
        }
    }

    // Helper function to get background color for a status
    const getStatusBackgroundColor = (statusName) => {
        const statusItem = statusOptions.find(item => item.title === statusName);
        if (statusItem?.color) return statusItem.color;
        return STATIC_STATUS_BG[statusName] || '#ECEEF4';
    };

    return (
        <div className="flex gap-3 items-center my-3">
            {/* Status Dropdown */}
            <Dropdown
                label="Status"
                options={statusOptions}
                selected={active_task?.status || statusOptions[0]?.title || 'Not Started'}
                selectedValue={active_task?.status || statusOptions[0]?.title || 'Not Started'}
                getBackgroundColor={getStatusBackgroundColor}
                onSelect={updateOnSelect}
                type="status"
            />
            <Dropdown
                label="Progress"
                options={progressOptions}
                selected={selectedProgress?.title || 'Not Defined'}
                selectedValue={Number(active_task?.progress ?? 0)}
                onSelect={updateOnSelect}
                type="progress"
            />
        </div>
    );
};

export default TaskStatusAndProgressNew;
